// Layout and form primitives every page is built from. Pages compose these
// rather than restyling raw elements, so spacing, type and states stay
// consistent across the app.

import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useIllustrations, type Moment } from '../lib/illustrations'
import { MomentArt } from './goblin-art'

export function PageHeader({
  title,
  description,
  actions,
  back,
  art,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** Small link rendered above the title, e.g. back to a list. */
  back?: ReactNode
  /** A goblin beside the title when illustrations are on. */
  art?: Moment
}) {
  return (
    <header className="mb-7 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="flex min-w-0 max-w-2xl items-center gap-4">
        {art ? <MomentArt moment={art} size={72} className="-my-2" /> : null}
        <div className="min-w-0">
          {back ? <div className="mb-2 text-sm text-ink-3">{back}</div> : null}
          <h1 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
            {title}
          </h1>
          {description ? (
            <p className="mt-1 text-base text-ink-2">{description}</p>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </header>
  )
}

/** A titled section of a page. `flush` removes inner padding for tables. */
export function Panel({
  title,
  description,
  actions,
  children,
  flush,
  className = '',
}: {
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  flush?: boolean
  className?: string
}) {
  return (
    <section className={`panel ${className}`}>
      {title ? (
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-3.5">
          <div>
            <h2 className="font-display text-md font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p className="mt-0.5 text-sm text-ink-3">{description}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </header>
      ) : null}
      <div className={flush ? '' : 'p-5'}>{children}</div>
    </section>
  )
}

/**
 * Empty lists are an invitation to act: say what goes here and how to get it
 * there. Shows a small goblin (`art`) when illustrations are on.
 */
export function EmptyState({
  title,
  children,
  action,
  art = 'empty',
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
  art?: Moment | null
}) {
  return (
    <div className="px-6 py-10 text-center">
      {art ? (
        <MomentArt moment={art} size={112} className="mx-auto mb-3" />
      ) : null}
      <p className="font-display text-md font-medium text-ink">{title}</p>
      {children ? (
        <p className="mx-auto mt-1 max-w-md text-base text-ink-2">{children}</p>
      ) : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  )
}

export function LoadingRows({ label = 'Loading…' }: { label?: string }) {
  return <p className="px-5 py-8 text-center text-base text-ink-3">{label}</p>
}

export function ErrorNote({ error, what }: { error: unknown; what: string }) {
  const message = error instanceof Error ? error.message : String(error)
  return (
    <div className="px-5 py-8 text-center">
      <p className="text-base font-medium text-danger">Couldn’t load {what}.</p>
      <p className="mt-1 text-sm text-ink-3">{message}</p>
    </div>
  )
}

export function Notice({
  tone = 'info',
  children,
  onDismiss,
}: {
  tone?: 'info' | 'success' | 'error' | 'signal'
  children: ReactNode
  onDismiss?: () => void
}) {
  const tones = {
    info: 'border-info/30 bg-info-soft text-ink',
    success: 'border-ok/30 bg-ok-soft text-ink',
    error: 'border-danger/30 bg-danger-soft text-ink',
    signal: 'border-signal/30 bg-signal-soft text-ink',
  }
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start justify-between gap-4 rounded-control border px-4 py-2.5 text-base ${tones[tone]}`}
    >
      <div>{children}</div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-ink-3 hover:text-ink"
        >
          Dismiss
        </button>
      ) : null}
    </div>
  )
}

export function Field({
  label,
  hint,
  children,
  htmlFor,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
  htmlFor?: string
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-base font-medium text-ink"
      >
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1.5 text-sm text-ink-3">{hint}</p> : null}
    </div>
  )
}

/** Label/value pairs for summaries. */
export function Facts({
  items,
  columns = 2,
}: {
  /** `icon`: an optional small icon left of the label and value. */
  items: { label: string; value: ReactNode; icon?: ReactNode }[]
  columns?: 2 | 3 | 4
}) {
  const cols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  }[columns]
  return (
    <dl className={`grid grid-cols-1 gap-x-6 gap-y-4 ${cols}`}>
      {items.map((item) => (
        <div key={item.label} className="flex min-w-0 items-start gap-2.5">
          {item.icon}
          <div className="min-w-0">
            <dt className="text-sm text-ink-3">{item.label}</dt>
            <dd className="mt-0.5 truncate text-base text-ink">{item.value}</dd>
          </div>
        </div>
      ))}
    </dl>
  )
}

export interface TabOption<T extends string> {
  value: T
  label: string
  count?: number
}

/** Segmented filter / tab bar. Tabs are real buttons with aria-pressed. */
export function Tabs<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: TabOption<T>[]
  value: T
  onChange: (value: T) => void
  label: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex flex-wrap gap-1 border-b border-line"
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-3 pb-2.5 pt-1 text-base font-medium transition-colors ${
              active
                ? 'border-moss text-ink'
                : 'border-transparent text-ink-3 hover:text-ink'
            }`}
          >
            {option.label}
            {option.count != null && option.count > 0 ? (
              <span
                className={`rounded-full px-1.5 text-xs font-semibold ${active ? 'bg-moss-soft text-moss' : 'bg-sunken text-ink-3'}`}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Right-hand slide-over for create/edit/detail flows. Keeps the list visible
 * behind it so you never lose your place. Escape and the backdrop close it.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: string
}) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  // In a ref so a new callback on re-render doesn't re-run the effect below
  // (which would move focus back to the first field while someone works).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    // `data-no-autofocus` opts a control out (e.g. the report version picker).
    panelRef.current
      ?.querySelector<HTMLElement>(
        'input:not([data-no-autofocus]), textarea:not([data-no-autofocus]), select:not([data-no-autofocus]), button[data-autofocus]'
      )
      ?.focus()
    function onKey(event: KeyboardEvent): void {
      // A field inside that already used Escape (e.g. to close its
      // suggestions), or a Dialog on top, marks it handled; then the panel
      // stays open.
      if (event.key === 'Escape' && !event.defaultPrevented) onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-30 flex justify-end">
      <div
        className="absolute inset-0 bg-ink/25 motion-safe:animate-[fade_120ms_ease-out]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative flex h-full w-full ${width} flex-col border-l border-line bg-surface shadow-overlay motion-safe:animate-[slide-in_180ms_cubic-bezier(.2,.8,.2,1)]`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="font-display text-lg font-semibold text-ink"
            >
              {title}
            </h2>
            {description ? (
              <p className="mt-0.5 text-base text-ink-2">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost btn-sm -mr-2"
            aria-label="Close"
          >
            Close
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-line px-6 py-3.5">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  )
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusableIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.getClientRects().length > 0
  )
}

/**
 * Centered modal for focused, blocking tasks (e.g. the executor login
 * terminal). Focus moves in on open (`data-autofocus`, else the first
 * control), Tab is trapped inside, and focus returns on close.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  art,
  size = 'lg',
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /** Goblin shown above the title when illustrations are on (a /goblins/*.webp path). */
  art?: string
  size?: 'md' | 'lg'
  children: ReactNode
}) {
  const titleId = useId()
  const [illustrations] = useIllustrations()
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    // Initial focus: an element marked `data-autofocus`, else the first
    // focusable one — unless something inside (e.g. a terminal) already
    // took focus while mounting.
    if (panel && !panel.contains(document.activeElement)) {
      ;(
        panel.querySelector<HTMLElement>('[data-autofocus]') ??
        focusableIn(panel)[0] ??
        panel
      ).focus()
    }
    function onKey(event: KeyboardEvent): void {
      // A control inside that already handled the key (a field closing its
      // suggestions on Escape, a terminal taking Tab) wins.
      if (event.defaultPrevented) return
      if (event.key === 'Escape') {
        // Handled here: a Drawer underneath (listening on window, which
        // fires after document) must not close too.
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key === 'Tab' && panel) {
        // Focus trap: Tab / Shift+Tab cycle within the dialog.
        const items = focusableIn(panel)
        if (items.length === 0) {
          event.preventDefault()
          panel.focus()
          return
        }
        const first = items[0]!
        const last = items[items.length - 1]!
        const active = document.activeElement
        if (event.shiftKey && (active === first || !panel.contains(active))) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    // On document (bubble): after the dialog's own fields, before window listeners.
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
  }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative w-full outline-none ${size === 'md' ? 'max-w-lg' : 'max-w-2xl'} max-h-[calc(100vh-2rem)] overflow-y-auto rounded-panel border border-line bg-surface shadow-overlay`}
      >
        {art && illustrations ? (
          <div className="flex justify-center bg-moss-soft pb-1 pt-5">
            <img
              src={art}
              alt=""
              aria-hidden
              width={112}
              height={112}
              className="h-28 w-28 select-none object-contain"
            />
          </div>
        ) : null}
        <div className="p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2
                id={titleId}
                className="font-display text-lg font-semibold text-ink"
              >
                {title}
              </h2>
              {description ? (
                <p className="mt-1 text-base text-ink-2">{description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost btn-sm -mr-2"
            >
              Close
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

/** A UUID shown only when there's no name for it; full id on hover. */
export function IdText({ id }: { id: string }) {
  return (
    <span className="mono-id" title={id}>
      {id.slice(0, 8)}
    </span>
  )
}
